import React from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { BuildingSelector, ScheduleSelector, ZoneSelector } from 'components';
import { useServiceInterruptionFormManager } from './service-interruption-form.hook';

interface ServiceInterruptionFormProps {
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const ServiceInterruptionForm = React.forwardRef(
  ({ onSubmitting = () => {}, next }: ServiceInterruptionFormProps, ref) => {
    const [{ formManager, loading_submit }] = useServiceInterruptionFormManager({
      next,
    });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return (
      <Box sx={{ paddingY: 2 }}>
        <form onSubmit={formManager.handleSubmit} autoComplete="off">
          <Grid container sx={{ alignItems: 'center' }} spacing={3}>
            <Grid item xs={12}>
              <BuildingSelector
                value={formManager.values.buildingId}
                onChange={(value) => {
                  if (value !== formManager.values.buildingId) {
                    formManager.setFieldValue('buildingId', value);
                    formManager.setFieldValue('zoneId', null);
                  }
                }}
                error={!!(formManager.errors.buildingId && formManager.touched.buildingId)}
                helperText={(formManager.touched.buildingId ? formManager.errors.buildingId : '') as string}
              />
            </Grid>

            <Grid item xs={12}>
              {formManager.values.buildingId ? (
                <ZoneSelector
                  buildingId={formManager.values.buildingId}
                  value={formManager.values.zoneId}
                  onChange={(value) => {
                    formManager.setFieldValue('zoneId', value);
                  }}
                  error={!!(formManager.errors.scheduleId && formManager.touched.scheduleId)}
                  helperText={(formManager.touched.scheduleId ? formManager.errors.scheduleId : '') as string}
                />
              ) : (
                <Tooltip title="A building must be selected first" arrow>
                  <Autocomplete
                    options={[]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Zone"
                        inputProps={{ ...params.inputProps, sx: { cursor: 'not-allowed' } }}
                      />
                    )}
                    fullWidth
                    disabled
                  />
                </Tooltip>
              )}
            </Grid>

            <Grid item xs={12}>
              {formManager.values.zoneId ? (
                <ScheduleSelector
                  zoneId={formManager.values.zoneId}
                  value={formManager.values.scheduleId}
                  onChange={(value) => {
                    formManager.setFieldValue('scheduleId', value);
                  }}
                  error={!!(formManager.errors.scheduleId && formManager.touched.scheduleId)}
                  helperText={(formManager.touched.scheduleId ? formManager.errors.scheduleId : '') as string}
                />
              ) : (
                <Tooltip title="A zone must be selected first" arrow>
                  <Autocomplete
                    options={[]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Schedule"
                        inputProps={{ ...params.inputProps, sx: { cursor: 'not-allowed' } }}
                        error={!!(formManager.errors.scheduleId && formManager.touched.scheduleId)}
                        helperText={(formManager.touched.scheduleId ? formManager.errors.scheduleId : '') as string}
                      />
                    )}
                    fullWidth
                    disabled
                  />
                </Tooltip>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comment"
                placeholder="Add comment..."
                variant="outlined"
                value={formManager.values.comment}
                onChange={formManager.handleChange('comment')}
                error={!!(formManager.errors.comment && formManager.touched.comment)}
                helperText={(formManager.touched.comment ? formManager.errors.comment : '') as string}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ paddingTop: 2, paddingBottom: 1 }}>
                <Typography variant="body2">
                  {`Does this service interruption impact all zone schedules at this building?`}
                </Typography>
              </Box>

              <FormControl sx={{ width: '100%' }}>
                <RadioGroup
                  defaultValue={'no'}
                  value={formManager.values.impactsBuilding ? 'yes' : 'no'}
                  row
                  onChange={(e) => formManager.setFieldValue('impactsBuilding', e.target.value === 'yes')}
                  sx={{ flexDirection: 'row' }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={<Radio value={'yes'} />}
                        label={
                          <Box sx={{ paddingY: 1.3 }}>
                            <Typography variant="body2">Yes</Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControlLabel
                        control={<Radio value={'no'} />}
                        label={
                          <Box sx={{ paddingY: 1.3 }}>
                            <Typography variant="body2">No</Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  },
);
